import { t } from '@lingui/core/macro';
import {
  type InfiniteData,
  useInfiniteQuery,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { createFileRoute, redirect, useLocation } from '@tanstack/react-router';
import { Mouse } from '@untitledui/icons/Mouse';
import { Plus } from '@untitledui/icons/Plus';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

import type {
  Project,
  ProjectRecruitingStatus,
  ProjectsPaginatedDto,
  ProjectStatus,
} from '@/api/core-api/types.gen';

import { KindOfResearch, ProjectSortField } from '@/api/core-api/types.gen';
import { Button } from '@/components/base/buttons/button';
import { AddToProgramModal } from '@/components/programs/add-to-program-modal';
import { ProgramFormModal } from '@/components/programs/program-form-modal';
import { ResearchHeader } from '@/components/programs/research-header';
import {
  ProjectCard,
  ProjectCardSkeleton,
} from '@/components/projects/project-card';
import { ProjectListTable } from '@/components/projects/project-list-table';
import {
  ProjectListToolbar,
  STATUS_OPTIONS,
  type ViewMode,
} from '@/components/projects/project-list-toolbar';
import { organizationCanUseAiQueryOptions } from '@/hooks/api/use-organization';
import { useCreateProgram } from '@/hooks/api/use-program-mutations';
import { programsQueryOptions } from '@/hooks/api/use-programs';
import { useProjectMessagesRealtime } from '@/hooks/api/use-project-messages-realtime';
import { useProjectUnreadCounts } from '@/hooks/api/use-project-unread-counts';
import {
  DEFAULT_PAGE_SIZE,
  teamProjectsInfiniteQueryOptions,
} from '@/hooks/api/use-team-projects';
import { useLoadMoreSentinel } from '@/hooks/use-load-more-sentinel';
import { useProjectListActions } from '@/hooks/use-project-list-actions';
import { useViewportConstrainedBodyHeight } from '@/hooks/use-viewport-constrained-body-height';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import {
  AI_MODERATED_INTERVIEWS_FLAG,
  orgFeatureFlagQueryOptions,
} from '@/lib/feature-flags/client/org-feature-flag-query-options';
import { isomorphicOrgFeatureFlagQueryOptions } from '@/lib/feature-flags/isomorphic-org-feature-flag-query-options';
import { getPublicEnv } from '@/utils/session/get-public-env';

import { AiInterviewerBanner } from './-components/ai-interviewer-banner';
import { NewProjectSetupModal } from './-components/new-project-setup-modal';
import { ProjectSearchInput } from './-components/project-search-input';
import { projectsViewPreference } from './-components/project-view-storage';
import { ResearcherProjectsLayout } from './-components/researcher-projects-layout';

// --- URL search schema ---

const STATUS_FILTER_IDS = [
  'draft',
  'pending',
  'recruiting',
  'paused',
  'recruited',
  'closed',
  'archived',
] as const;

type StatusFilterId = (typeof STATUS_FILTER_IDS)[number];

const projectsSearchSchema = z.object({
  // The list loads on scroll, so `page` is a reading position, not a server
  // page swap: it is the page whose rows sit at the top of the viewport. A
  // deep link starts the list there (earlier pages load on scrolling up), and
  // the URL is kept in sync as the researcher scrolls.
  page: z.number().int().positive().catch(1).default(1),
  // TanStack Router JSON-parses search params, so `?q=123` arrives as a
  // number — coerce so numeric queries don't crash schema validation.
  q: z.coerce.string().max(255).optional().catch(undefined),
  statuses: z.array(z.enum(STATUS_FILTER_IDS)).catch([]).default([]),
  kindsOfResearch: z.array(z.coerce.number().int()).catch([]).default([]),
  mine: z.boolean().catch(false).default(false),
  sortDir: z.enum(['asc', 'desc']).catch('desc').default('desc'),
  // Optional so an entry without an explicit view (e.g. the sidebar link) can
  // be seeded from the researcher's persisted preference after mount.
  view: z.enum(['card', 'list']).optional().catch(undefined),
  // Opens the new-project setup modal; used by external entry points (e.g.
  // the legacy app's "New project" button) to deep-link into project creation.
  create: z.boolean().optional(),
});

export type ProjectsSearch = z.infer<typeof projectsSearchSchema>;

// --- Filter ID → API param translation (reused by route loader and component) ---

function buildStatusApiParams(selectedIds: Set<StatusFilterId>): {
  statuses?: ProjectStatus[];
  recruitingStatuses?: ProjectRecruitingStatus[];
  closed?: boolean;
} {
  if (selectedIds.size === 0) return {};

  const statuses = new Set<ProjectStatus>();
  const recruitingStatuses: ProjectRecruitingStatus[] = [];
  let includeClosed = false;

  for (const id of selectedIds) {
    const opt = STATUS_OPTIONS.find((o) => o.id === id);
    if (!opt?.status) continue;

    statuses.add(opt.status);

    if (opt.recruitingStatus) {
      recruitingStatuses.push(opt.recruitingStatus);
    }
    if (opt.closed === true) {
      includeClosed = true;
    }
  }

  return {
    statuses: statuses.size > 0 ? [...statuses] : undefined,
    recruitingStatuses:
      recruitingStatuses.length > 0 ? recruitingStatuses : undefined,
    closed: includeClosed
      ? true
      : recruitingStatuses.length > 0
        ? false
        : undefined,
  };
}

const VALID_KINDS = new Set<number>(Object.values(KindOfResearch));

function buildTypeApiParams(selectedIds: Set<number>) {
  if (selectedIds.size === 0) return {};
  return {
    kindsOfResearch: [...selectedIds].filter((n): n is KindOfResearch =>
      VALID_KINDS.has(n),
    ),
  };
}

const EMPTY_PROJECTS: Project[] = [];
const EMPTY_PROJECT_IDS: string[] = [];

// Module-level so its identity is stable: TanStack Query re-runs `select`
// whenever the function reference changes, and this route re-renders on every
// URL page update while scrolling.
function selectLoadedProjects(data: InfiniteData<ProjectsPaginatedDto>) {
  const projects = data.pages.flatMap((page) => page.results ?? []);
  return {
    projects,
    projectIds: projects.map((p) => p.id).toSorted(),
    // The latest page carries the freshest count.
    totalResults: data.pages.at(-1)?.totalResults ?? projects.length,
    firstLoadedPage: data.pages[0]?.page ?? 1,
    lastLoadedPage: data.pages.at(-1)?.page ?? 1,
  };
}

function mapSearchToApiParams(
  search: ProjectsSearch,
  authUserId: string,
  teamId: string,
) {
  const statusApi = buildStatusApiParams(new Set(search.statuses));
  const typeApi = buildTypeApiParams(new Set(search.kindsOfResearch));

  return {
    teamId,
    authUserId,
    sortBy: ProjectSortField.CREATED_AT,
    sortDir: search.sortDir,
    statuses: statusApi.statuses,
    recruitingStatuses: statusApi.recruitingStatuses,
    closed: statusApi.closed,
    kindsOfResearch: typeApi.kindsOfResearch,
    name: search.q && search.q.length >= 3 ? search.q : undefined,
    userId: search.mine ? authUserId : undefined,
  };
}

export const Route = createFileRoute('/researchers/_authenticated/projects/')({
  validateSearch: projectsSearchSchema,
  beforeLoad: async ({ context }) => {
    const organizationId = context.activeDashboard.organization ?? undefined;
    const teamId = context.activeDashboard.team ?? undefined;

    if (context.v3Enabled !== true) {
      const env = await getPublicEnv();
      if (env.WEBSITE_URL.length > 0 && organizationId && teamId) {
        const legacyHref = `${env.WEBSITE_URL}/dashboard/organizations/${organizationId}/teams/${teamId}/projects`;
        throw redirect({ href: legacyHref });
      }
    }
  },
  component: ProjectListPage,
  head: () => ({ meta: [{ title: t`Projects` }] }),
});

function ProjectListPage() {
  const { user, activeDashboard, env } = Route.useRouteContext();
  const teamId = activeDashboard.team ?? undefined;
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const analytics = useAnalytics();
  const queryClient = useQueryClient();
  // The history entry's key: the same entry comes back on Back/Forward, so
  // the table can restore the exact reading position for it.
  const historyKey = useLocation({ select: (l) => l.state.__TSR_key });

  // The AI Moderator banner is an AI entry point: the org flag AND no AI
  // opt-out, both required, and off while either is still resolving.
  const [aiModFlag, aiPolicy] = useQueries({
    queries: [
      orgFeatureFlagQueryOptions(
        activeDashboard.organization,
        AI_MODERATED_INTERVIEWS_FLAG,
      ),
      organizationCanUseAiQueryOptions(activeDashboard.organization),
    ],
  });
  const showAiInterviewerBanner =
    aiModFlag.data === true && aiPolicy.data === true;

  // Programs promo — discovery lives here because researchers do. Same org
  // flag and cache entry the sidebar resolves, so this is usually a cache read.
  const { data: programsEnabled = false } = useQuery(
    isomorphicOrgFeatureFlagQueryOptions(
      activeDashboard.organization,
      'research-programs',
      user.id,
    ),
  );
  const [isCreateProgramOpen, setIsCreateProgramOpen] = useState(false);
  const [addToProgramProjectId, setAddToProgramProjectId] = useState<string>();
  const createProgram = useCreateProgram(teamId ?? '', user.id);
  const { data: programs } = useQuery({
    ...programsQueryOptions(teamId ?? '', user.id),
    enabled: programsEnabled && !!teamId,
  });

  // Effective view for rendering. URL wins while on the page; the persisted
  // preference is applied via the effect below when the URL has no view.
  const view = search.view ?? 'list';

  // Restore the persisted view preference when entering the route without an
  // explicit `view` param. Runs after mount (localStorage is unavailable
  // during SSR); replaces the URL so history stays clean.
  useEffect(() => {
    if (search.view !== undefined) return;
    const stored = projectsViewPreference.get();
    if (stored !== 'list') {
      void navigate({
        search: (prev) => ({ ...prev, view: stored }),
        replace: true,
      });
    }
  }, [search.view, navigate]);

  const isNewProjectModalOpen = search.create === true;
  const setIsNewProjectModalOpen = useCallback(
    (open: boolean) => {
      void navigate({
        search: (prev) => ({ ...prev, create: open || undefined }),
        // Push when opening so the (clean) list stays in history and Back from
        // the prompt page returns to it; replace when closing to collapse the
        // `?create=true` entry back to a clean list URL.
        replace: !open,
      });
    },
    [navigate],
  );

  const openNewProject = useCallback(
    (origin: 'header' | 'empty_state' | 'banner') => {
      analytics?.ui({
        action: 'clicked',
        actionSubject: 'newProject',
        source: 'projectListPage',
        attributes: { source: 'projects_list', origin },
      });
      setIsNewProjectModalOpen(true);
    },
    [analytics, setIsNewProjectModalOpen],
  );

  const updateSearch = useCallback(
    (updates: Partial<ProjectsSearch>, opts?: { resetPage?: boolean }) => {
      void navigate({
        search: (prev) => ({
          ...prev,
          ...updates,
          // A filter/sort change is a new list; start it from the top.
          ...(opts?.resetPage === false ? {} : { page: 1 }),
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const commitSearch = useCallback(
    (next: string | undefined) => {
      if (next === search.q) return;
      analytics?.ui({
        action: 'searched',
        actionSubject: 'projects',
        source: 'projectListPage',
        attributes: { has_query: next !== undefined },
      });
      updateSearch({ q: next });
    },
    [analytics, search.q, updateSearch],
  );

  const apiParams = useMemo(
    () =>
      teamId
        ? mapSearchToApiParams(search, user.id, teamId)
        : { teamId: '', authUserId: '' },
    [search, user.id, teamId],
  );

  const infiniteOptions = useMemo(
    () => teamProjectsInfiniteQueryOptions(apiParams, search.page),
    [apiParams, search.page],
  );

  // Scroll-to-load: pages accumulate in one query as the researcher scrolls,
  // in both directions from the URL's page. Any filter/sort change is a new
  // key (and resets `page`), so the list starts over from the top. The URL
  // page is deliberately not part of the key: it changes as the researcher
  // scrolls, and only seeds the first fetch of an empty cache entry.
  const {
    data,
    isPending,
    isError,
    refetch,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    hasPreviousPage,
    isFetchingPreviousPage,
    fetchPreviousPage,
  } = useInfiniteQuery({
    ...infiniteOptions,
    enabled: !!apiParams.teamId,
    select: selectLoadedProjects,
  });

  const projects = data?.projects ?? EMPTY_PROJECTS;
  const projectIds = data?.projectIds ?? EMPTY_PROJECT_IDS;
  const totalResults = data?.totalResults ?? 0;
  const firstLoadedPage = data?.firstLoadedPage ?? search.page;
  const lastLoadedPage = data?.lastLoadedPage ?? search.page;
  const isFetchingMore = isFetchingNextPage || isFetchingPreviousPage;

  // Guarded here so the list views can call them unconditionally; `undefined`
  // when there is nothing to load tells them to stop watching.
  const loadMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    analytics?.ui({
      action: 'paginated',
      actionSubject: 'projects',
      source: 'projectListPage',
      attributes: { page: lastLoadedPage + 1, mode: 'scroll' },
    });
    void fetchNextPage();
  }, [
    analytics,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    lastLoadedPage,
  ]);
  const loadPrevious = useCallback(() => {
    if (!hasPreviousPage || isFetchingPreviousPage) return;
    analytics?.ui({
      action: 'paginated',
      actionSubject: 'projects',
      source: 'projectListPage',
      attributes: { page: firstLoadedPage - 1, mode: 'scroll' },
    });
    void fetchPreviousPage();
  }, [
    analytics,
    hasPreviousPage,
    isFetchingPreviousPage,
    fetchPreviousPage,
    firstLoadedPage,
  ]);
  const onNearEnd = hasNextPage ? loadMore : undefined;
  const onNearStart = hasPreviousPage ? loadPrevious : undefined;
  const cardGridSentinelRef = useLoadMoreSentinel(
    view === 'card' ? onNearEnd : undefined,
  );

  // Mirror the reading position into the URL (replace, so Back still leaves
  // the list) as the table reports which row sits at the top of its scroller.
  const handleTopRowChange = useCallback(
    (index: number) => {
      const page = firstLoadedPage + Math.floor(index / DEFAULT_PAGE_SIZE);
      if (page !== search.page) {
        updateSearch({ page }, { resetPage: false });
      }
    },
    [firstLoadedPage, search.page, updateSearch],
  );

  // Where the URL's page starts within the loaded rows, fixed from the first
  // data that arrives. A fresh deep link fetches that page first, so the index
  // is 0 and any earlier pages loaded afterwards are handled by the table's
  // prepend anchoring; a warm cache already holds earlier pages, so the index
  // points into them and the table scrolls there once. Not recomputed later:
  // scrolling rewrites `page`, and following it would re-scroll a list the
  // researcher is reading.
  const [initialTopIndex, setInitialTopIndex] = useState<number>();
  useEffect(() => {
    if (initialTopIndex !== undefined || !data) return;
    setInitialTopIndex(
      Math.max(0, (search.page - data.firstLoadedPage) * DEFAULT_PAGE_SIZE),
    );
  }, [initialTopIndex, data, search.page]);

  // A stale deep link past the end returns an empty page while the count says
  // there are projects: fall back to the last page that exists. Rewriting the
  // URL alone isn't enough — the page is not in the query key, so the cache
  // still holds only the empty page; reset it so the query restarts from the
  // corrected page. Once per mount, so a count that shrinks between the two
  // requests can't loop.
  const didResetForStalePage = useRef(false);
  useEffect(() => {
    if (isPending || projects.length > 0 || totalResults === 0) return;
    const lastPage = Math.max(1, Math.ceil(totalResults / DEFAULT_PAGE_SIZE));
    if (search.page > lastPage) {
      updateSearch({ page: lastPage }, { resetPage: false });
      return;
    }
    if (didResetForStalePage.current) return;
    didResetForStalePage.current = true;
    void queryClient.resetQueries({
      queryKey: infiniteOptions.queryKey,
      exact: true,
    });
  }, [
    isPending,
    projects.length,
    totalResults,
    search.page,
    updateSearch,
    queryClient,
    infiniteOptions.queryKey,
  ]);

  // List view: size the table body to the viewport so the column header and
  // the status footer stay on screen and only the rows scroll. Card view
  // keeps page scrolling (a grid has no single scroll body to constrain).
  const [tableArea, setTableArea] = useState<HTMLElement | null>(null);
  const footerRef = useRef<HTMLDivElement | null>(null);
  const maxBodyHeight = useViewportConstrainedBodyHeight(tableArea, footerRef);

  const openAddToProgram = useCallback(
    (project: Project) => setAddToProgramProjectId(project.id),
    [],
  );

  const { data: inboxSummary } = useProjectUnreadCounts(
    teamId,
    projectIds,
    user.id,
  );

  // Keep the per-project unread badges live: refetch counts when the messaging
  // service broadcasts a new message on any visible project's channel.
  useProjectMessagesRealtime({
    appKey: env.PUSHER_APP_KEY,
    teamId,
    projectIds,
  });

  const { callbacks: projectActions, dialog: projectActionsDialog } =
    useProjectListActions(teamId, programsEnabled);

  const projectAlerts = useMemo(() => {
    const map: Record<
      string,
      { unreadMessages: number; outstandingPayments: number }
    > = {};
    for (const p of projects) {
      const attended = p.metrics?.screeners?.status?.attended ?? 0;
      const paid = p.metrics?.screeners?.status?.paid ?? 0;
      map[p.id] = {
        outstandingPayments: Math.max(attended - paid, 0),
        unreadMessages: inboxSummary?.projectUnreadCounts?.[p.id] ?? 0,
      };
    }
    return map;
  }, [projects, inboxSummary]);

  const handleViewChange = useCallback(
    (next: ViewMode) => {
      if (next === view) return;

      projectsViewPreference.set(next);

      analytics?.ui({
        action: 'toggled',
        actionSubject: 'projectViewMode',
        source: 'projectListPage',
        // Plan taxonomy uses 'grid' for the card layout.
        attributes: { view_mode: next === 'card' ? 'grid' : 'list' },
      });

      const direction = next === 'list' ? 'forward' : 'backward';

      if (!document.startViewTransition) {
        updateSearch({ view: next });
        return;
      }

      document.documentElement.dataset.viewDirection = direction;
      const transition = document.startViewTransition(async () => {
        await navigate({
          search: (prev) => ({ ...prev, view: next }),
          replace: true,
        });
      });
      void transition.finished.finally(() => {
        delete document.documentElement.dataset.viewDirection;
      });
    },
    [analytics, view, navigate, updateSearch],
  );

  const handleStatusChange = useCallback(
    (set: Set<string>) => {
      const statuses = [...set].filter((id): id is StatusFilterId =>
        (STATUS_FILTER_IDS as readonly string[]).includes(id),
      );
      analytics?.ui({
        action: 'filtered',
        actionSubject: 'projectStatus',
        source: 'projectListPage',
        attributes: { statuses: statuses.join(',') },
      });
      updateSearch({ statuses });
    },
    [analytics, updateSearch],
  );

  const handleTypeChange = useCallback(
    (set: Set<string>) => {
      const kindsOfResearch = [...set].map(Number);
      analytics?.ui({
        action: 'filtered',
        actionSubject: 'projectType',
        source: 'projectListPage',
        attributes: { project_types: kindsOfResearch.join(',') },
      });
      updateSearch({ kindsOfResearch });
    },
    [analytics, updateSearch],
  );

  const handleMyProjectsToggle = useCallback(
    (enabled: boolean) => {
      analytics?.ui({
        action: 'toggled',
        actionSubject: 'myProjectsOnly',
        source: 'projectListPage',
        attributes: { enabled },
      });
      updateSearch({ mine: enabled });
    },
    [analytics, updateSearch],
  );

  const handleSortToggle = useCallback(() => {
    const sortDir = search.sortDir === 'asc' ? 'desc' : 'asc';
    analytics?.ui({
      action: 'sorted',
      actionSubject: 'projects',
      source: 'projectListPage',
      attributes: { sort_field: 'created_at', sort_dir: sortDir },
    });
    updateSearch({ sortDir });
  }, [analytics, search.sortDir, updateSearch]);

  // Scroll-to-load status. The spinner sits beside the count rather than
  // replacing it so nothing flickers on each page. "scroll to load more", not
  // "Load more": the list lazy-loads on scroll and this control mostly signals
  // that affordance. It is a real button so keyboard/AT users who can't
  // trigger the scroll-based load still can.
  const loadMoreStatus = (
    <>
      {isFetchingMore && (
        <div
          className="size-3.5 animate-spin rounded-full border-2 border-secondary border-t-brand-solid"
          aria-hidden
        />
      )}
      <span>
        {programsEnabled
          ? t`Showing ${projects.length} of ${totalResults} studies`
          : t`Showing ${projects.length} of ${totalResults} projects`}
      </span>
      {hasNextPage && !isFetchingNextPage && (
        <Button
          iconLeading={Mouse}
          onClick={loadMore}
          size="xs"
          color="tertiary"
          className="font-light text-gray-400"
        >
          {t`scroll to load more`}
        </Button>
      )}
    </>
  );

  return (
    <ResearcherProjectsLayout>
      <NewProjectSetupModal
        isOpen={isNewProjectModalOpen}
        onOpenChange={setIsNewProjectModalOpen}
        organizationId={activeDashboard.organization}
        useStudyTerminology={programsEnabled}
      />
      {/* Create-a-program from the shared Research header. Lands the
          researcher on the new program's page, where the empty state offers
          the next step. */}
      <ProgramFormModal
        isOpen={isCreateProgramOpen}
        onOpenChange={(open) => {
          setIsCreateProgramOpen(open);
          if (!open) createProgram.reset();
        }}
        isSaving={createProgram.isPending}
        errorMessage={
          createProgram.isError ? t`Could not create the program.` : undefined
        }
        onSubmit={(values) => {
          createProgram.mutate(values, {
            onSuccess: (program) => {
              setIsCreateProgramOpen(false);
              void navigate({
                to: '/researchers/programs/$programId',
                params: { programId: program.id },
              });
            },
          });
        }}
      />
      {teamId && addToProgramProjectId && (
        <AddToProgramModal
          isOpen
          onOpenChange={(open) => {
            if (!open) setAddToProgramProjectId(undefined);
          }}
          teamId={teamId}
          authUserId={user.id}
          projectId={addToProgramProjectId}
        />
      )}
      <div className="px-6 py-8">
        <div className="mx-auto max-w-360">
          {programsEnabled && teamId ? (
            <ResearchHeader
              activeTab="projects"
              teamId={teamId}
              authUserId={user.id}
              onNewProject={() => openNewProject('header')}
              onNewProgram={() => setIsCreateProgramOpen(true)}
            />
          ) : (
            <div className="flex items-center justify-between">
              <h1 className="font-heading text-display-xs font-semibold text-primary">
                {t`Projects`}
              </h1>
              <Button
                size="sm"
                color="primary"
                iconLeading={Plus}
                data-testid="researcher-projects-new-button"
                onClick={() => openNewProject('header')}
              >
                {t`New project`}
              </Button>
            </div>
          )}

          {/* AI Moderator promo — gated by the org's AI-moderated interviews flag */}
          {showAiInterviewerBanner && !programsEnabled && (
            <div className="mt-6">
              <AiInterviewerBanner onStart={() => openNewProject('banner')} />
            </div>
          )}

          {/* Toolbar */}
          <div className="mt-6">
            <ProjectListToolbar
              viewMode={view}
              onViewModeChange={handleViewChange}
              selectedStatusIds={new Set<string>(search.statuses)}
              onStatusChange={handleStatusChange}
              selectedTypeIds={new Set(search.kindsOfResearch.map(String))}
              onTypeChange={handleTypeChange}
              searchSlot={
                <ProjectSearchInput
                  urlQuery={search.q ?? ''}
                  onCommit={commitSearch}
                  useStudyTerminology={programsEnabled}
                />
              }
              myProjectsOnly={search.mine}
              onMyProjectsToggle={handleMyProjectsToggle}
              useStudyTerminology={programsEnabled}
            />
          </div>

          <div className="mt-6">
            {isError ? (
              <div className="rounded-xl border border-secondary bg-primary p-12 text-center">
                <p className="text-sm text-tertiary">
                  {programsEnabled
                    ? t`Failed to load studies.`
                    : t`Failed to load projects.`}
                </p>
                <Button
                  color="link-color"
                  size="sm"
                  className="mt-2"
                  onPress={() => refetch()}
                >
                  {t`Try again`}
                </Button>
              </div>
            ) : !isPending && totalResults === 0 ? (
              <div className="rounded-xl border border-secondary bg-primary p-12 text-center">
                <p className="mb-4 text-sm text-tertiary">
                  {programsEnabled
                    ? t`No studies matching the selected filters`
                    : t`No projects matching the selected filters`}
                </p>
                <Button
                  size="sm"
                  color="primary"
                  iconLeading={Plus}
                  onClick={() => openNewProject('empty_state')}
                >
                  {programsEnabled ? t`Create a study` : t`Create a project`}
                </Button>
              </div>
            ) : (
              <>
                <div
                  className="view-transition-panel"
                  aria-busy={isFetchingMore}
                >
                  {view === 'card' ? (
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                      {/* Earlier pages behind an explicit control, not a top
                          sentinel: a sentinel at the top is in view on load
                          and would chain-load every page back to the first. */}
                      {hasPreviousPage && !isPending && (
                        <div className="flex justify-center md:col-span-2 lg:col-span-3">
                          <Button
                            size="sm"
                            color="secondary"
                            onClick={loadPrevious}
                            isDisabled={isFetchingPreviousPage}
                          >
                            {programsEnabled
                              ? t`Load earlier studies`
                              : t`Load earlier projects`}
                          </Button>
                        </div>
                      )}
                      {isPending
                        ? Array.from({ length: 6 }, (_, i) => (
                            <ProjectCardSkeleton key={i} />
                          ))
                        : projects.map((project) => (
                            <ProjectCard
                              key={project.id}
                              project={project}
                              unreadMessages={
                                projectAlerts[project.id]?.unreadMessages ?? 0
                              }
                              outstandingPayments={
                                projectAlerts[project.id]
                                  ?.outstandingPayments ?? 0
                              }
                              onPause={projectActions.onPause}
                              onUnpause={projectActions.onUnpause}
                              onCopy={projectActions.onCopy}
                              onClose={projectActions.onClose}
                              onDelete={projectActions.onDelete}
                              onArchive={projectActions.onArchive}
                              useStudyTerminology={programsEnabled}
                            />
                          ))}
                    </div>
                  ) : (
                    <div ref={setTableArea}>
                      <ProjectListTable
                        projects={projects}
                        projectAlerts={projectAlerts}
                        isLoading={isPending}
                        createdSortDir={search.sortDir}
                        onCreatedSortToggle={handleSortToggle}
                        onPause={projectActions.onPause}
                        onUnpause={projectActions.onUnpause}
                        onCopy={projectActions.onCopy}
                        onClose={projectActions.onClose}
                        onDelete={projectActions.onDelete}
                        onArchive={projectActions.onArchive}
                        programs={
                          programsEnabled ? (programs ?? []) : undefined
                        }
                        onAddToProgram={openAddToProgram}
                        useStudyTerminology={programsEnabled}
                        onNearEnd={onNearEnd}
                        onNearStart={onNearStart}
                        onTopRowChange={handleTopRowChange}
                        maxBodyHeight={maxBodyHeight}
                        initialTopIndex={initialTopIndex}
                        restorationKey={
                          historyKey
                            ? `projects:${teamId ?? ''}:${historyKey}`
                            : undefined
                        }
                        footer={
                          // Always mounted (even while loading) so the height
                          // hook can observe it from the first measurement.
                          <div
                            ref={footerRef}
                            className="flex min-h-12 items-center justify-center gap-2 border-t border-secondary px-6 py-2 text-xs text-secondary"
                          >
                            {!isPending && loadMoreStatus}
                          </div>
                        }
                      />
                    </div>
                  )}
                </div>

                {/* Card view loads more via this sentinel below the grid; the
                    table has its own row-window lookahead and renders the
                    same status inside its card footer. */}
                {view === 'card' && !isPending && (
                  <div
                    ref={cardGridSentinelRef}
                    className="mt-4 flex items-center justify-center gap-2 text-xs text-secondary"
                  >
                    {loadMoreStatus}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {projectActionsDialog}
      </div>
    </ResearcherProjectsLayout>
  );
}
